<template>
  <div>
    <rest-page :fetch-endpoint="fetchEndpoint" ref="restPage">
      <rest-form
        :save-endpoint="saveRulesEndpoint"
        :after-save="afterSaveRules"
        :model="model"
        :rules="{}"
        :readonly="readonly"
        ref="rulesForm"
      >
        <template v-if="model">
          <form-section
            :title="$t('ProjectSettings_EngagementRules_Title')"
            :description="$t('ProjectSettings_EngagementRules_Description')"
            data-qa="engagement-rules-section"
          >
            <el-row>
              <el-col :span="18">
                <toggle-list :data="rulesList" :model="model" />
              </el-col>
            </el-row>
            <template slot="controls">
              <save-button :click="saveRules" />
            </template>
          </form-section>
        </template>
      </rest-form>

      <rest-form
        :save-endpoint="saveTrackingEndpoint"
        :after-save="afterSaveTracking"
        :model="model"
        :rules="{}"
        :readonly="readonly"
        ref="trackingForm"
      >
        <template v-if="model">
          <form-section
            :title="$t('ProjectSettings_EngagementTracking_Title')"
            :description="$t('ProjectSettings_EngagementTracking_Description')"
            data-qa="engagement-tracking-section"
          >
            <el-row>
              <el-col :span="18">
                <toggle-list :data="trackingList" :model="model" />
              </el-col>
            </el-row>
            <template slot="controls">
              <save-button :click="saveTracking" />
            </template>
          </form-section>
        </template>
      </rest-form>

      <form-section
        :title="$t('ProjectSettings_EngagementConditions_Title')"
        :description="$t('ProjectSettings_EngagementConditions_Description')"
        data-qa="engagement-conditions-section"
      >
        <toggle
          v-model="engagementConditionsEnabled"
          :text="$t('ProjectSettings_EngagementConditions_Enable')"
          data-qa="global-include-enable-toggle"
        />

        <conditions-editor
          class="m-top-20"
          ref="conditions-editor"
          v-if="engagementConditionsEnabled"
          v-model="engagementConditions"
          :menu="engagementConditionsMenu"
          data-model-name="Segment"
          data-model-attribute="conditions"
          attribute-list-data-url="/segment/segment/attribute-list-data"
          :single-level="true"
        />

        <template slot="controls">
          <save-button :click="saveEngagementConditions" />
        </template>
      </form-section>

      <form-section
        v-if="channelIncludeConditionSectionVisible"
        :title="$t('ProjectSettings_ChannelIncludeCondition_Title')"
        :description="$t('ProjectSettings_ChannelIncludeCondition_Description')"
        data-qa="channel-include-condition-section"
      >
        <channel-include-condition-slot
          v-for="slot in visibleChannelSlots"
          :key="slot.channel"
          :ref="`channel-slot-${slot.channel}`"
          :channel="slot.channel"
          :label="slot.label"
          :enabled="channelState[slot.channel].enabled"
          :conditions="channelState[slot.channel].conditions"
          :menu="engagementConditionsMenu"
          @update:enabled="value => onChannelEnabledChange(slot.channel, value)"
          @update:conditions="
            value => onChannelConditionsChange(slot.channel, value)
          "
        />

        <template slot="controls">
          <save-button :click="saveChannelEngagementConditions" />
        </template>
      </form-section>
    </rest-page>
  </div>
</template>

<script>
import { projectEndpoint } from '@/core/helpers'
import FormSection from '@/common/components/form/FormSection'
import RestPage from '@/common/components/rest/RestPage'
import RestForm from '@/common/components/rest/RestForm'
import ToggleList from '../../../common/components/form/ToggleList'
import SaveButton from '@/common/components/form/SaveButton'
import RestApi from '@/core/services/RestApi'
import Toggle from '@/common/components/form/Toggle'
import ConditionsEditor from '@/common/components/ConditionsEditor'
import ChannelIncludeConditionSlot from './ChannelIncludeConditionSlot'
import { emptyChannelConditions } from './channelIncludeConditions'

const CHANNEL_WEB_PUSH = 'web_push'
const CHANNEL_ANDROID_PUSH = 'android_push'
const CHANNEL_IOS_PUSH = 'ios_push'
const CHANNEL_EMAIL = 'email'
const CHANNEL_SMS = 'sms'
const CHANNEL_WEBHOOK = 'webhook'
const CHANNEL_WHATSAPP = 'whatsapp'
const CHANNEL_FACEBOOK = 'facebook'
const CHANNEL_INAPP = 'inapp'
const CHANNEL_ONSITE = 'onsite'
const CHANNEL_INBOX = 'inbox'
const SUPPORTED_CHANNELS = [
  CHANNEL_IOS_PUSH,
  CHANNEL_ANDROID_PUSH,
  CHANNEL_WEB_PUSH,
  CHANNEL_INAPP,
  CHANNEL_ONSITE,
  CHANNEL_INBOX,
  CHANNEL_EMAIL,
  CHANNEL_WEBHOOK,
  CHANNEL_SMS,
  CHANNEL_FACEBOOK,
  CHANNEL_WHATSAPP
]
const RELEASE_FLAG = 'channel-include-conditions'

const emptyChannelState = () =>
  SUPPORTED_CHANNELS.reduce((acc, channel) => {
    acc[channel] = { enabled: false, conditions: emptyChannelConditions() }
    return acc
  }, {})

export default {
  name: 'EngagementRulesTab',
  components: {
    ChannelIncludeConditionSlot,
    ConditionsEditor,
    Toggle,
    SaveButton,
    ToggleList,
    FormSection,
    RestPage,
    RestForm
  },

  data() {
    return {
      model: {},
      engagementConditions: null,
      engagementConditionsEnabled: null,
      channels: null,
      channelState: emptyChannelState(),
      savedGlobalSignature: null,
      savedChannelSignature: null
    }
  },

  computed: {
    fetchEndpoint() {
      if (this.$route.query.project_id) {
        return projectEndpoint({
          method: 'get',
          url: ``,
          params: {
            expand:
              'engagement_conditions, channel_engagement_conditions, channels, disable_email_open_tracking, disable_email_click_tracking'
          },
          handler: data => {
            this.model = {
              broadcast_disabled: data.broadcast_disabled,
              target_application_enabled: data.target_application_enabled,
              target_devices_enabled: data.target_devices_enabled,
              categories_required: data.categories_required,
              calculate_recipients: data.calculate_recipients,
              feature_seed_list: data.feature_seed_list,
              seed_list_enabled: data.seed_list_enabled,
              disable_email_open_tracking: data.disable_email_open_tracking,
              disable_email_click_tracking: data.disable_email_click_tracking
            }

            if (data.engagement_conditions) {
              this.engagementConditionsEnabled = 1
              this.engagementConditions = data.engagement_conditions
            } else {
              this.engagementConditionsEnabled = 0
              this.engagementConditions = emptyChannelConditions()
            }

            this.channels = data.channels || null
            this.channelState = this.buildChannelState(
              data.channel_engagement_conditions
            )

            this.snapshotGlobal()
            this.snapshotChannels()
          }
        })
      } else {
        return null
      }
    },

    saveRulesEndpoint() {
      return projectEndpoint({
        method: 'patch',
        url: ``,
        params: {
          data: this.model
        }
      })
    },

    saveTrackingEndpoint() {
      return projectEndpoint({
        method: 'patch',
        url: ``,
        params: {
          data: this.model
        }
      })
    },

    rulesList() {
      let rules = [
        {
          code: 'broadcast_disabled',
          name: this.$t('ProjectSettings_EngagementRules_BroadcastDisabled'),
          description: this.$t(
            'ProjectSettings_EngagementRules_BroadcastDisabled_Description'
          )
        },
        {
          code: 'calculate_recipients',
          name: this.$t('ProjectSettings_EngagementRules_CalculateRecipient'),
          description: this.$t(
            'ProjectSettings_EngagementRules_CalculateRecipient_Description'
          )
        },
        {
          code: 'categories_required',
          name: this.$t('ProjectSettings_EngagementRules_CategoriesRequired'),
          description: this.$t(
            'ProjectSettings_EngagementRules_CategoriesRequired_Description'
          )
        },
        {
          code: 'target_application_enabled',
          name: this.$t(
            'ProjectSettings_EngagementRules_TargetApplicationEnabled'
          ),
          description: this.$t(
            'ProjectSettings_EngagementRules_TargetApplicationEnabled_Description'
          )
        },
        {
          code: 'target_devices_enabled',
          name: this.$t('ProjectSettings_EngagementRules_TargetDevicesEnabled'),
          description: this.$t(
            'ProjectSettings_EngagementRules_TargetDevicesEnabled_Description'
          )
        }
      ]

      if (this.model.feature_seed_list) {
        rules.push({
          code: 'seed_list_enabled',
          name: this.$t('ProjectSettings_EngagementRules_SeedListEnabled'),
          description: this.$t(
            'ProjectSettings_EngagementRules_SeedListEnabled_Description'
          )
        })
      }

      return rules
    },

    trackingList() {
      return [
        {
          code: 'disable_email_open_tracking',
          name: this.$t('ProjectSettings_EngagementTracking_EmailOpenTracking'),
          description: this.$t(
            'ProjectSettings_EngagementTracking_EmailOpenTracking_Description'
          )
        },
        {
          code: 'disable_email_click_tracking',
          name: this.$t(
            'ProjectSettings_EngagementTracking_EmailClickTracking'
          ),
          description: this.$t(
            'ProjectSettings_EngagementTracking_EmailClickTracking_Description'
          )
        }
      ]
    },

    engagementConditionsMenu() {
      return {
        'main-choices': {
          tags_attribute: {
            title: 'Attribute',
            icon: 'attributes',
            has_submenu: false
          }
        }
      }
    },

    readonly() {
      return !this.$can('manageProjectTechnical')
    },

    channelIncludeConditionSectionVisible() {
      return (
        this.$hasReleaseFlag(RELEASE_FLAG) &&
        this.visibleChannelSlots.length > 0
      )
    },

    visibleChannelSlots() {
      if (!this.channels) {
        return []
      }
      const slots = []
      if (this.channels.app_push && this.channels.app_push.ios_push) {
        slots.push({
          channel: CHANNEL_IOS_PUSH,
          label: this.$t('ProjectSettings_ChannelIncludeCondition_IosPush')
        })
      }
      if (this.channels.app_push && this.channels.app_push.android_push) {
        slots.push({
          channel: CHANNEL_ANDROID_PUSH,
          label: this.$t('ProjectSettings_ChannelIncludeCondition_AndroidPush')
        })
      }
      if (this.channels.web_push && this.channels.web_push.enabled) {
        slots.push({
          channel: CHANNEL_WEB_PUSH,
          label: this.$t('ProjectSettings_ChannelIncludeCondition_WebPush')
        })
      }
      if (this.channels.inapp && this.channels.inapp.enabled) {
        slots.push({
          channel: CHANNEL_INAPP,
          label: this.$t('ProjectSettings_ChannelIncludeCondition_InApp')
        })
      }
      if (this.channels.onsite && this.channels.onsite.enabled) {
        slots.push({
          channel: CHANNEL_ONSITE,
          label: this.$t('ProjectSettings_ChannelIncludeCondition_Onsite')
        })
      }
      if (this.channels.inbox && this.channels.inbox.enabled) {
        slots.push({
          channel: CHANNEL_INBOX,
          label: this.$t('ProjectSettings_ChannelIncludeCondition_Inbox')
        })
      }
      if (this.channels.email && this.channels.email.enabled) {
        slots.push({
          channel: CHANNEL_EMAIL,
          label: this.$t('ProjectSettings_ChannelIncludeCondition_Email')
        })
      }
      if (this.channels.webhook && this.channels.webhook.enabled) {
        slots.push({
          channel: CHANNEL_WEBHOOK,
          label: this.$t('ProjectSettings_ChannelIncludeCondition_Webhook')
        })
      }
      if (this.channels.sms && this.channels.sms.enabled) {
        slots.push({
          channel: CHANNEL_SMS,
          label: this.$t('ProjectSettings_ChannelIncludeCondition_Sms')
        })
      }
      if (this.channels.facebook && this.channels.facebook.enabled) {
        slots.push({
          channel: CHANNEL_FACEBOOK,
          label: this.$t('ProjectSettings_ChannelIncludeCondition_Facebook')
        })
      }
      if (this.channels.whatsapp && this.channels.whatsapp.enabled) {
        slots.push({
          channel: CHANNEL_WHATSAPP,
          label: this.$t('ProjectSettings_ChannelIncludeCondition_WhatsApp')
        })
      }
      return slots
    },

    globalHasChanges() {
      return (
        this.savedGlobalSignature !== null &&
        this.currentGlobalSignature() !== this.savedGlobalSignature
      )
    },

    channelHasChanges() {
      return (
        this.savedChannelSignature !== null &&
        this.currentChannelSignature() !== this.savedChannelSignature
      )
    },

    hasUnsavedChanges() {
      return this.globalHasChanges || this.channelHasChanges
    }
  },

  mounted() {
    window.addEventListener('beforeunload', this.beforeUnloadHandler)
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.beforeUnloadHandler)
  },

  beforeRouteLeave(to, from, next) {
    if (!this.hasUnsavedChanges) {
      next()
      return
    }
    this.confirmUnsavedChanges()
      .then(() => next())
      .catch(() => next(false))
  },

  methods: {
    afterSaveRules(resp, loading) {
      this.$store.dispatch('reloadProjectConfig').then(loading())
    },

    afterSaveTracking(resp, loading) {
      this.$store.dispatch('reloadProjectConfig').then(loading())
    },

    switcher() {},

    saveRules() {
      this.$refs['rulesForm'].save()
    },

    saveTracking() {
      this.$refs['trackingForm'].save()
    },

    saveEngagementConditions() {
      this.updateEngagementConditions(
        this.engagementConditionsEnabled
          ? this.$refs['conditions-editor'].serialize()
          : null
      )
    },

    updateEngagementConditions(conditions) {
      this.$refs.restPage.loading = true
      RestApi(
        projectEndpoint({
          method: 'patch',
          url: ``,
          data: { engagement_conditions: conditions }
        })
      )
        .then(() => {
          this.$refs.restPage.loading = false
          this.snapshotGlobal()
          this.$message({
            message: this.$t(
              'ProjectSettings_EngagementConditions_SuccessfullySaved'
            ),
            type: 'success'
          })
        })
        .catch(error => {
          this.$refs.restPage.loading = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    buildChannelState(payload) {
      const state = emptyChannelState()
      if (!payload || typeof payload !== 'object') {
        return state
      }
      SUPPORTED_CHANNELS.forEach(channel => {
        const conditions = payload[channel]
        if (conditions) {
          state[channel] = { enabled: true, conditions }
        }
      })
      return state
    },

    onChannelEnabledChange(channel, enabled) {
      const current = this.channelState[channel]
      this.$set(this.channelState, channel, {
        enabled,
        conditions: enabled ? current.conditions : emptyChannelConditions()
      })
    },

    onChannelConditionsChange(channel, conditions) {
      const current = this.channelState[channel]
      this.$set(this.channelState, channel, {
        enabled: current.enabled,
        conditions
      })
    },

    saveChannelEngagementConditions() {
      const payload = {}
      const validationErrors = []
      this.visibleChannelSlots.forEach(slot => {
        const state = this.channelState[slot.channel]
        if (!state.enabled) {
          return
        }
        const ref = this.$refs[`channel-slot-${slot.channel}`]
        const slotComponent = Array.isArray(ref) ? ref[0] : ref
        if (!slotComponent) {
          return
        }
        const errors = slotComponent.validate()
        if (errors.length) {
          errors.forEach(err =>
            validationErrors.push(`${slot.label}: ${err.message}`)
          )
          return
        }
        const serialized = slotComponent.serialize()
        if (serialized) {
          payload[slot.channel] = serialized
        }
      })

      if (validationErrors.length) {
        this.$message({
          message: validationErrors.join('<br>'),
          type: 'error',
          dangerouslyUseHTMLString: true
        })
        return
      }

      this.$refs.restPage.loading = true
      RestApi(
        projectEndpoint({
          method: 'patch',
          url: ``,
          data: {
            channel_engagement_conditions:
              Object.keys(payload).length === 0 ? null : payload
          }
        })
      )
        .then(() => {
          this.$refs.restPage.loading = false
          this.snapshotChannels()
          this.$message({
            message: this.$t(
              'ProjectSettings_ChannelIncludeCondition_SuccessfullySaved'
            ),
            type: 'success'
          })
        })
        .catch(error => {
          this.$refs.restPage.loading = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    currentGlobalSignature() {
      return JSON.stringify({
        enabled: !!this.engagementConditionsEnabled,
        conditions: this.engagementConditionsEnabled
          ? this.engagementConditions
          : null
      })
    },

    currentChannelSignature() {
      return JSON.stringify(this.channelState)
    },

    snapshotGlobal() {
      this.savedGlobalSignature = this.currentGlobalSignature()
    },

    snapshotChannels() {
      this.savedChannelSignature = this.currentChannelSignature()
    },

    beforeUnloadHandler(event) {
      if (!this.hasUnsavedChanges) {
        return undefined
      }
      event.preventDefault()
      event.returnValue = ''
      return ''
    },

    confirmUnsavedChanges() {
      return this.$msgbox({
        title: this.$t('CampaignForm_Confirm_Title'),
        message: this.$t(
          'ProjectSettings_IncludeCondition_UnsavedChanges_Message'
        ),
        showCancelButton: true,
        confirmButtonText: this.$t(
          'ProjectSettings_IncludeCondition_UnsavedChanges_Discard'
        ),
        cancelButtonText: this.$t(
          'ProjectSettings_IncludeCondition_UnsavedChanges_KeepEditing'
        ),
        distinguishCancelAndClose: true,
        center: true
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/modules/settings/components/EngagementRulesTab.vue